// frontend/src/Components/SyncTo/ReviewModal.jsx
//
// Phase 32 — Review/Apply gate for Note-mode SyncTo.
// Phase 33 — EXTENDED with Template variant (mode === 'template').
//
// NOTE VARIANT (Phase 32 — unchanged):
//   Parent (RecordingProvider) opens the modal after /transcribe succeeds,
//   passing the cleaned Doctor:/Patient: paragraphs as props. User edits the
//   textarea, optionally flips speaker labels, then clicks Apply — which POSTs
//   to /api/sync-to/apply via useSyncToApi.apply().
//
// TEMPLATE VARIANT (Phase 33 — NEW):
//   Parent opens the modal after /transcribe Template-branch succeeds, passing
//   routedValues (AI-proposed per-element values), dropped (elements absent
//   from AI response), existingCapturedValues (session's current state), and
//   templatePages (pages[] for label/type lookup + NoteRenderer preview).
//   Modal renders a per-element review table with Accept checkboxes. Below
//   the table a <details> pane shows NoteRenderer in preview mode with the
//   merged capturedValues overlay ({ ...existing, ...acceptedProposed }) so
//   the doctor sees exactly what the session will look like after Apply.
//   PITFALL #12 invariant: NoteRenderer's readonlyValues overlay is read-only
//   for the preview — the real mutation happens server-side in /apply.
//
// Invariants:
//   - Does NOT manage show internally; parent controls open/close.
//   - Does NOT call onClose on Apply-success; parent handles close via onApplied.
//   - Speaker-swap (Note variant) operates on the TEXTAREA CURRENT VALUE (D-09)
//     — preserves user inline edits. __SYNCTO_TMP__ sentinel is clinically impossible.
//   - No PHI logging — zero console.* calls in this file (T-32-27 / T-33-17).
//   - Renders via Form.Control as=textarea / plain text nodes (no innerHTML) —
//     no unsafe HTML injection (T-32-22 / T-33-16).
//   - Template variant: acceptedFields submitted to /apply contains ONLY rows
//     where Accept checkbox is checked (T-33-18 — unchecked rows excluded
//     client-side; server call #2 re-validates regardless).

import React, { useState, useEffect } from 'react';
import { Modal, Button, Alert, Form, Table } from 'react-bootstrap';
import { useSyncToApi } from './hooks/useSyncToApi';
import NoteRenderer from '../Templates/NoteRenderer';

// D-09 sentinel — chosen to be clinically impossible (double-underscore
// identifier is not a valid medical token in en-ZA or af-ZA clinical corpus).
const SPEAKER_SWAP_SENTINEL = '__SYNCTO_TMP__';

// D-10 banner copy — locked to the exact string from CONTEXT.md specifics.
const CLEANUP_FAILED_TEXT =
  'Auto-cleanup unavailable — please add speaker labels and review carefully before applying.';

// D-06 idempotent info banner.
const IDEMPOTENT_TEXT = 'Already applied — no change.';

// Phase 34 SC7 — flag-flipped-off-mid-session banner copy (locked literal —
// matches ROADMAP Phase 34 Success Criterion #7 verbatim). Surfaced when the
// /apply endpoint returns 403 with the exact requireSyncToFeatureFlag message.
const FEATURE_DISABLED_TEXT = 'This feature has been disabled for your account.';
const FEATURE_DISABLED_STATUS = 403;
const FEATURE_DISABLED_MESSAGE = 'SyncTo is not enabled for this user';

// Phase 33 — allowlist for Template variant. Matches backend contract:
// validateRoutedValues in syncToService.js accepts ONLY these types. All other
// template element types (radio, yes_no, numeric, pain_scale, dropdown, date,
// image, photo_upload, title, divider, static_text, spacer) are rendered as
// greyed "Not routed — please fill manually (type: <type>)" rows.
const TEMPLATE_ALLOWLIST_TYPES = ['text', 'memo', 'checkbox'];

/**
 * D-09 exact speaker-swap algorithm.
 * Three-step string replace with an intermediate sentinel so Doctor↔Patient
 * swap without collision. Preserves any user-typed custom labels verbatim
 * (Dr. Smith:, Pt.: etc. are NOT flipped — documented D-09 known limit).
 */
function swapSpeakers(input) {
  if (typeof input !== 'string') return input;
  return input
    .split('Doctor:').join(SPEAKER_SWAP_SENTINEL)
    .split('Patient:').join('Doctor:')
    .split(SPEAKER_SWAP_SENTINEL).join('Patient:');
}

/**
 * Phase 33 — flatten a page's items into the allowlist-visible items.
 * Walks page-level elements AND group children so the Template variant table
 * shows every element the user might want to accept. Groups are never
 * filtered — only the element's `type` determines allowlist membership
 * (checked downstream in the JSX loop).
 *
 * Pure function, no side effects, order preserved (stable render order =
 * stable row order between re-renders; checkedIds is keyed by element id so
 * reordering mid-flow is safe, but we keep this deterministic for UX).
 *
 * Mirrors the routeTemplateValues allowlist walker in syncToService.js
 * (Phase 33-01) — same flattening rule, different purpose (here: render;
 * there: prompt-build).
 */
function getAllowlistItems(page) {
  const items = [];
  for (const item of (page?.items || [])) {
    if (item?.kind === 'element') items.push(item);
    if (item?.kind === 'group') {
      for (const child of (item?.children || [])) items.push(child);
    }
  }
  return items;
}

/**
 * Phase 33 — flatten existingCapturedValues ({ id: { value, fieldType, capturedAt } })
 * into { id: value } for NoteRenderer's readonlyValues overlay. NoteRenderer
 * expects flat values, NOT the wrapper object — PITFALL #12 invariant.
 */
function flattenExistingCaptured(existing) {
  if (!existing || typeof existing !== 'object') return {};
  const out = {};
  for (const key of Object.keys(existing)) {
    const entry = existing[key];
    if (entry && typeof entry === 'object' && 'value' in entry) {
      out[key] = entry.value;
    } else {
      // Defensive — if shape drifts, still expose whatever value is there.
      out[key] = entry;
    }
  }
  return out;
}

export default function ReviewModal({
  show,
  onClose,
  onApplied,
  mode,
  sessionId,
  // Note-variant props (Phase 32 — unchanged)
  cleaned,
  cleanupFailed,
  transcript,
  durationMs,
  // Template-variant props (Phase 33)
  templateId,
  templatePages,
  routedValues,
  dropped,
  existingCapturedValues,
}) {
  // Note-variant state (Phase 32 — unchanged semantics)
  const [text, setText] = useState(cleaned || '');
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState(null);
  const [appliedResult, setAppliedResult] = useState(null);
  // Phase 34 SC7 — set on 403 'SyncTo is not enabled for this user' from /apply.
  // Swaps the footer to Cancel-only and renders the FEATURE_DISABLED_TEXT banner.
  const [featureDisabled, setFeatureDisabled] = useState(false);
  // Template-variant state (Phase 33)
  const [checkedIds, setCheckedIds] = useState(() => new Set());
  const [editedValues, setEditedValues] = useState({});
  const { apply } = useSyncToApi();

  // Re-initialise state when the modal re-opens with fresh data.
  useEffect(() => {
    if (!show) return;
    setApplying(false);
    setError(null);
    setAppliedResult(null);
    // Phase 34 SC7 — clear the disabled-banner state on every re-open so a
    // subsequent session (after auth re-validates the flag) gets a clean UI.
    setFeatureDisabled(false);
    // Note variant
    setText(cleaned || '');
    // Template variant — pre-fill editedValues from routedValues; pre-check
    // rows that have a routed value and are NOT in dropped[].
    if (mode === 'template') {
      const initEdited = {};
      const initChecked = new Set();
      const droppedIds = new Set((dropped || []).map((d) => d && d.elementId).filter(Boolean));
      for (const page of (templatePages || [])) {
        for (const item of getAllowlistItems(page)) {
          if (!TEMPLATE_ALLOWLIST_TYPES.includes(item?.type)) continue;
          if (droppedIds.has(item.id)) continue;
          if (routedValues && Object.prototype.hasOwnProperty.call(routedValues, item.id)) {
            const v = routedValues[item.id];
            initEdited[item.id] = item.type === 'checkbox'
              ? !!v
              : (v === null || v === undefined ? '' : String(v));
            initChecked.add(item.id);
          }
        }
      }
      setEditedValues(initEdited);
      setCheckedIds(initChecked);
    } else {
      setEditedValues({});
      setCheckedIds(new Set());
    }
  }, [show, cleaned, mode, templatePages, routedValues, dropped]);

  const onSwap = () => setText(swapSpeakers(text));

  const onApply = async () => {
    // Template variant — Phase 33
    if (mode === 'template') {
      if (checkedIds.size === 0 || !sessionId) return;
      setApplying(true);
      setError(null);
      const acceptedFields = Object.fromEntries(
        Object.entries(editedValues).filter(([id]) => checkedIds.has(id)),
      );
      const { data, error: err } = await apply({
        sessionId,
        mode: 'template',
        templateId,
        acceptedFields,
        transcript,
        durationMs,
      });
      setApplying(false);
      if (err) {
        // Phase 34 SC7 — detect the flag-flipped-off-mid-session response.
        if (err.status === FEATURE_DISABLED_STATUS && err.message === FEATURE_DISABLED_MESSAGE) {
          setFeatureDisabled(true);
          setError(null);
          if (typeof onApplied === 'function') onApplied({ featureDisabled: true });
          return;
        }
        setError(err.message || 'Apply failed');
        return;
      }
      setAppliedResult(data);
      if (onApplied) onApplied(data);
      return;
    }
    // Note variant — Phase 32 (unchanged)
    if (!text.trim() || !sessionId) return;
    setApplying(true);
    setError(null);
    const { data, error: err } = await apply({
      sessionId,
      mode: mode || 'note',
      cleanedText: text,
      transcript,
      durationMs,
    });
    setApplying(false);
    if (err) {
      // Phase 34 SC7 — detect the flag-flipped-off-mid-session response.
      if (err.status === FEATURE_DISABLED_STATUS && err.message === FEATURE_DISABLED_MESSAGE) {
        setFeatureDisabled(true);
        setError(null);
        if (typeof onApplied === 'function') onApplied({ featureDisabled: true });
        return;
      }
      setError(err.message || 'Apply failed');
      return;
    }
    setAppliedResult(data);
    if (onApplied) onApplied(data);
  };

  // Template variant — compute rows for render and NoteRenderer overlay.
  // Placed at component body level so render stays cheap; no memo needed at
  // this scale (templates are typically < 50 elements).
  const isTemplate = mode === 'template';
  const droppedIdSet = isTemplate
    ? new Set((dropped || []).map((d) => d && d.elementId).filter(Boolean))
    : null;
  const flatExistingCaptured = isTemplate ? flattenExistingCaptured(existingCapturedValues) : {};
  const acceptedProposed = isTemplate
    ? Object.fromEntries(
        Object.entries(editedValues).filter(([id]) => checkedIds.has(id)),
      )
    : {};
  const previewValues = isTemplate
    ? { ...flatExistingCaptured, ...acceptedProposed }
    : {};

  // Drop the "Current" column entirely when no reviewed row has an existing
  // value. Walks every allowlist + non-allowlist item across pages so a single
  // filled field keeps the column visible.
  const hasAnyCurrent = (() => {
    if (!isTemplate) return false;
    for (const page of (templatePages || [])) {
      for (const item of getAllowlistItems(page)) {
        const entry = existingCapturedValues && existingCapturedValues[item.id];
        const raw = entry && typeof entry === 'object' && !Array.isArray(entry) && 'value' in entry
          ? entry.value
          : entry;
        if (raw !== undefined && raw !== null && raw !== '') return true;
      }
    }
    return false;
  })();
  const templateColCount = hasAnyCurrent ? 4 : 3;

  // Toggle helper for Accept checkbox — Set is immutable to trigger React re-render.
  const toggleCheck = (id, checked) => {
    setCheckedIds((prev) => {
      const s = new Set(prev);
      if (checked) s.add(id); else s.delete(id);
      return s;
    });
  };

  const renderTemplateRow = (item) => {
    const id = item.id;
    const label = item.label || '(no label)';
    // Render existing current-value cell as plain text (never innerHTML — T-33-16)
    const existingEntry = existingCapturedValues && existingCapturedValues[id];
    // Sessions store capturedValues as a flat { [elementId]: value } map
    // (sessionsRoutes.js Phase 4). Defensively also unwrap the richer
    // { value, fieldType, capturedAt } shape in case a future writer uses it.
    const currentRaw = existingEntry && typeof existingEntry === 'object' && !Array.isArray(existingEntry) && 'value' in existingEntry
      ? existingEntry.value
      : existingEntry;
    const currentDisplay =
      currentRaw === undefined || currentRaw === null || currentRaw === ''
        ? <span className="text-muted">—</span>
        : (typeof currentRaw === 'boolean'
            ? (currentRaw ? 'Yes' : 'No')
            : String(currentRaw));

    // Not-in-allowlist row — greyed, no Accept checkbox
    if (!TEMPLATE_ALLOWLIST_TYPES.includes(item.type)) {
      return (
        <tr key={id} className="text-muted">
          <td>{label}</td>
          {hasAnyCurrent && <td>{currentDisplay}</td>}
          <td colSpan={2}>
            <span className="text-muted fst-italic">
              Not routed — please fill manually (type: {item.type || 'unknown'})
            </span>
          </td>
        </tr>
      );
    }

    // Absent-from-AI-response (dropped) row — greyed, no Accept checkbox
    if (droppedIdSet && droppedIdSet.has(id)) {
      return (
        <tr key={id} className="text-muted">
          <td>{label}</td>
          {hasAnyCurrent && <td>{currentDisplay}</td>}
          <td colSpan={2}>
            <span className="text-muted fst-italic">
              Not routed — please fill manually
            </span>
          </td>
        </tr>
      );
    }

    // Allowlisted + routed row — editable proposed value + Accept checkbox
    const isChecked = checkedIds.has(id);
    const memoText = editedValues[id] !== undefined ? String(editedValues[id]) : '';
    // Auto-size memo: expand to fit all content so the table row never clips
    // the text. Estimate wrapped-line count (~80 chars/line in an xl modal
    // textarea) on top of explicit newlines, with a generous minimum.
    const memoRows = Math.max(
      8,
      memoText.split('\n').reduce(
        (sum, line) => sum + Math.max(1, Math.ceil(line.length / 80)),
        0,
      ) + 1,
    );
    const proposedCell = item.type === 'checkbox' ? (
      <Form.Check
        type="switch"
        id={`syncto-tpl-switch-${id}`}
        checked={!!editedValues[id]}
        onChange={(e) =>
          setEditedValues((prev) => ({ ...prev, [id]: e.target.checked }))
        }
        disabled={applying || !isChecked}
        aria-label={`Proposed value for ${label}`}
      />
    ) : (
      <Form.Control
        as={item.type === 'memo' ? 'textarea' : 'input'}
        rows={item.type === 'memo' ? memoRows : undefined}
        value={editedValues[id] !== undefined ? editedValues[id] : ''}
        onChange={(e) =>
          setEditedValues((prev) => ({ ...prev, [id]: e.target.value }))
        }
        disabled={applying || !isChecked}
        aria-label={`Proposed value for ${label}`}
        style={item.type === 'memo' ? { height: 'auto', resize: 'vertical' } : undefined}
      />
    );

    return (
      <tr key={id}>
        <td style={{ verticalAlign: 'top' }}>{label}</td>
        {hasAnyCurrent && <td style={{ verticalAlign: 'top' }}>{currentDisplay}</td>}
        <td style={{ verticalAlign: 'top' }}>{proposedCell}</td>
        <td style={{ verticalAlign: 'middle', textAlign: 'center' }}>
          <Form.Check
            type="checkbox"
            id={`syncto-tpl-accept-${id}`}
            checked={isChecked}
            onChange={(e) => toggleCheck(id, e.target.checked)}
            disabled={applying}
            aria-label={`Accept proposed value for ${label}`}
          />
        </td>
      </tr>
    );
  };

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      scrollable
      dialogClassName="modal-85w"
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {mode === 'template' ? 'Review template fields' : 'Review transcript'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {cleanupFailed && mode !== 'template' && (
          <Alert variant="warning" className="mb-3">
            {CLEANUP_FAILED_TEXT}
          </Alert>
        )}
        {appliedResult && appliedResult.idempotent && (
          <Alert variant="info" className="mb-3 py-2 small">
            {IDEMPOTENT_TEXT}
          </Alert>
        )}
        {error && (
          <Alert variant="danger" className="mb-3">
            {error}
          </Alert>
        )}
        {featureDisabled && (
          <Alert variant="warning" className="mb-3" role="alert">
            {FEATURE_DISABLED_TEXT}
          </Alert>
        )}

        {mode === 'template' ? (
          <>
            <p className="small text-muted mb-2">
              Review each AI-proposed value, edit if needed, and tick{' '}
              <strong>Accept</strong> for the fields you want written to the session.
              Fields marked <em>Not routed</em> must be filled manually on the template
              tab after applying.
            </p>
            <Table bordered size="sm" className="align-middle">
              <thead>
                <tr>
                  <th style={{ width: hasAnyCurrent ? '22%' : '25%' }}>Field</th>
                  {hasAnyCurrent && <th style={{ width: '22%' }}>Current</th>}
                  <th>Proposed</th>
                  <th style={{ width: '80px', textAlign: 'center' }}>Accept</th>
                </tr>
              </thead>
              <tbody>
                {(templatePages || []).map((page, pageIdx) => (
                  <React.Fragment key={page?.pageId || `page-${pageIdx}`}>
                    {(templatePages || []).length > 1 && page?.pageTitle && (
                      <tr>
                        <th colSpan={templateColCount} className="bg-light text-secondary small">
                          {page.pageTitle}
                        </th>
                      </tr>
                    )}
                    {getAllowlistItems(page).map((item) => renderTemplateRow(item))}
                  </React.Fragment>
                ))}
              </tbody>
            </Table>
            <details className="mt-3">
              <summary className="small text-primary" style={{ cursor: 'pointer' }}>
                Preview — how the template will look after Apply
              </summary>
              <div className="mt-2 border rounded p-2" style={{ background: '#fafbfc' }}>
                <NoteRenderer
                  mode="preview"
                  pages={templatePages || []}
                  readonlyValues={previewValues}
                />
              </div>
            </details>
          </>
        ) : (
          <Form.Group>
            <Form.Label className="small text-muted mb-1">
              Edit before applying. Use <strong>Swap speakers</strong> to flip Doctor/Patient labels.
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={16}
              value={text}
              onChange={(e) => setText(e.target.value)}
              disabled={applying}
              aria-label="Transcript review textarea"
            />
          </Form.Group>
        )}
      </Modal.Body>
      <Modal.Footer>
        {mode === 'template' ? (
          <div className="d-flex justify-content-between w-100">
            <Button variant="outline-secondary" onClick={onClose} disabled={applying}>
              Cancel
            </Button>
            {!featureDisabled && (
              <Button
                variant="primary"
                onClick={onApply}
                disabled={applying || checkedIds.size === 0 || !sessionId}
              >
                {applying ? 'Applying\u2026' : `Apply (${checkedIds.size})`}
              </Button>
            )}
          </div>
        ) : (
          <div className="d-flex justify-content-between w-100">
            <Button variant="outline-secondary" onClick={onClose} disabled={applying}>
              Cancel
            </Button>
            {!featureDisabled && (
              <div className="d-flex gap-2">
                {!cleanupFailed && (
                  <Button
                    variant="outline-primary"
                    onClick={onSwap}
                    disabled={applying || !text.trim()}
                  >
                    Swap speakers
                  </Button>
                )}
                <Button
                  variant="primary"
                  onClick={onApply}
                  disabled={applying || !text.trim() || !sessionId}
                >
                  {applying ? 'Applying\u2026' : 'Apply'}
                </Button>
              </div>
            )}
          </div>
        )}
      </Modal.Footer>
    </Modal>
  );
}
